'use client'

import { useEffect, useState } from 'react'
import { trackModelShowcaseInteraction } from '@/lib/analytics'

/**
 * Anchors for the model page. It carries a lot — takes, capabilities, rates and
 * a 13-report write-up — and without this you scroll past all of it to find the
 * one you came for.
 *
 * Wide screens get a rail floating in the margin beside the content, which is
 * where the write-up puts its contents too. Anything narrower gets the same
 * links centred above the content, since there is no margin to float into.
 */
const SECTIONS = [
  { id: 'films', label: 'Films' },
  { id: 'capabilities', label: 'What it does' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'techniques', label: 'Techniques' },
]

export function Flux3SectionNav() {
  const [active, setActive] = useState<string | null>(null)
  const jumped = (id: string) =>
    trackModelShowcaseInteraction({ model: 'flux-3', interaction: 'section_nav', target: id })

  // A rail that floats permanently but never says where you are is worse than
  // no rail — so it tracks the topmost section currently on screen.
  useEffect(() => {
    const nodes = SECTIONS.map(s => document.getElementById(s.id)).filter((n): n is HTMLElement => !!n)
    if (!nodes.length) return

    const io = new IntersectionObserver(
      entries => {
        const onScreen = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (onScreen[0]) setActive(onScreen[0].target.id)
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 },
    )
    nodes.forEach(n => io.observe(n))
    return () => io.disconnect()
  }, [])

  return (
    <>
      <nav
        aria-label="On this page"
        className="pointer-events-none fixed start-6 top-1/2 z-30 hidden -translate-y-1/2 xl:block"
      >
        <ul className="pointer-events-auto flex flex-col gap-0.5 border-s border-border ps-px">
          {SECTIONS.map(s => {
            const on = active === s.id
            return (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  onClick={() => jumped(s.id)}
                  aria-current={on ? 'true' : undefined}
                  className={`-ms-px block border-s-2 py-1.5 pe-3 ps-3 text-xs transition-colors ${
                    on
                      ? 'border-foreground text-foreground'
                      : 'border-transparent text-muted-foreground/60 hover:bg-secondary hover:text-foreground'
                  }`}
                >
                  {s.label}
                </a>
              </li>
            )
          })}
        </ul>
      </nav>

      <nav aria-label="On this page" className="mx-auto w-full max-w-4xl px-6 pt-14 md:px-8 xl:hidden">
        <ul className="flex flex-wrap items-center justify-center gap-2">
          {SECTIONS.map(s => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={() => jumped(s.id)}
                className="block rounded px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </>
  )
}
